/**
 * Sprint 2.2 — Model-selector image generation panel.
 *
 * Uses the /images/generate-ai endpoint which resolves the provider via
 * the user's model preferences (DALL-E 3, Stability AI, Flux).
 * Tracks quota from /images/quota and history from /images/generated.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Image as ImageIcon, Download, Zap, AlertCircle, RefreshCw, Sparkles } from 'lucide-react';
import {
  GlassCard,
  GlassButton,
  GlassSelect,
  GlassBadge,
} from '../ui';
import {
  generateImageAi,
  getGeneratedImagesAi,
  getImageQuota,
  AiGeneratedImage,
  ImageQuota,
} from '../../services/api';
import './ImageGenerationPanel.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


// ── Constants ────────────────────────────────────────────────────────────────

const PLATFORM_OPTIONS = [
  { value: 'default',           label: 'Default (1024 × 1024)' },
  { value: 'instagram_square',  label: 'Instagram Square (1024 × 1024)' },
  { value: 'instagram_story',   label: 'Instagram Story (1024 × 1792)' },
  { value: 'facebook',          label: 'Facebook (1200 × 630)' },
  { value: 'linkedin',          label: 'LinkedIn (1200 × 627)' },
  { value: 'twitter',           label: 'Twitter / X (1600 × 900)' },
];

const QUALITY_OPTIONS = [
  { value: 'standard', label: 'Standard' },
  { value: 'hd',       label: 'HD (uses more credits)' },
];

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function providerLabel(provider: string): string {
  const map: Record<string, string> = {
    dall_e:     'DALL-E 3',
    stability:  'Stability AI',
    replicate:  'Flux (Replicate)',
    google:     'Google Imagen',
  };
  return map[provider] || provider;
}

// ── Component ─────────────────────────────────────────────────────────────────

const ImageGenerationPanel: React.FC = () => {
  // Form state
  const [prompt, setPrompt] = useState('');
  const [platform, setPlatform] = useState('default');
  const [quality, setQuality] = useState('standard');
  const [styleHint, setStyleHint] = useState('');

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<AiGeneratedImage | null>(null);
  const [error, setError] = useState('');

  // Quota & history
  const [quota, setQuota] = useState<ImageQuota | null>(null);
  const [history, setHistory] = useState<AiGeneratedImage[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // ── Load quota + history on mount ────────────────────────────────────────
  const loadData = useCallback(async () => {
    try {
      const q = await getImageQuota();
      setQuota(q);
    } catch {
      // quota display is non-critical
    }
    setHistoryLoading(true);
    try {
      const imgs = await getGeneratedImagesAi({ limit: 10 });
      setHistory(imgs);
    } catch {
      // history is non-critical
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Generate ─────────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    setError('');
    setResult(null);
    try {
      const res = await generateImageAi({
        prompt: prompt.trim(),
        platform,
        quality,
        style_hint: styleHint.trim() || undefined,
      });
      setResult(res);
      // Refresh quota and history after a successful generation
      loadData();
    } catch (e: any) {
      const detail = getApiError(e, 'Image generation failed');
      if (e?.response?.status === 429) {
        setError('Daily image generation limit reached. Upgrade your plan for more.');
      } else {
        setError(detail);
      }
    } finally {
      setGenerating(false);
    }
  };

  // ── Quota display helpers ─────────────────────────────────────────────────
  const quotaPercent =
    quota && !quota.unlimited && quota.daily_limit
      ? (quota.used_today / quota.daily_limit) * 100
      : 0;
  const quotaNearLimit = quotaPercent >= 80;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="igp-root">
      <div className="igp-layout">
        {/* Left: form */}
        <GlassCard>
          <div className="igp-form">
            <div className="igp-field">
              <label className="igp-label">Prompt</label>
              <textarea
                className="igp-textarea"
                placeholder="Describe the image you want to create..."
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                rows={5}
              />
            </div>

            <div className="igp-row-2">
              <GlassSelect
                label="Platform"
                options={PLATFORM_OPTIONS}
                value={platform}
                onChange={setPlatform}
                placeholder="Select platform"
              />
              <GlassSelect
                label="Quality"
                options={QUALITY_OPTIONS}
                value={quality}
                onChange={setQuality}
                placeholder="Select quality"
              />
            </div>

            <div className="igp-field">
              <label className="igp-label">Style guidance (optional)</label>
              <input
                className="igp-input"
                type="text"
                placeholder="e.g. photorealistic, watercolor, minimalist..."
                value={styleHint}
                onChange={e => setStyleHint(e.target.value)}
              />
            </div>

            {error && (
              <div className="igp-error">
                <AlertCircle size={14} />
                {error}
              </div>
            )}

            <GlassButton
              variant="primary"
              icon={<ImageIcon size={16} />}
              onClick={handleGenerate}
              disabled={generating || !prompt.trim()}
              style={{ width: '100%' }}
            >
              {generating ? 'Generating...' : 'Generate Image'}
            </GlassButton>

            {/* Quota badge */}
            {quota && (
              <div className={`igp-quota${quotaNearLimit ? ' igp-quota--warn' : ''}`}>
                <Zap size={13} />
                {quota.unlimited
                  ? 'Unlimited generations'
                  : `${quota.used_today} / ${quota.daily_limit} generated today`}
                <GlassBadge variant={quotaNearLimit ? 'warning' : 'default'}>
                  {quota.tier}
                </GlassBadge>
              </div>
            )}
          </div>
        </GlassCard>

        {/* Right: result */}
        <div className="igp-result-col">
          <GlassCard>
            {generating && (
              <div className="igp-loading">
                <div className="igp-spinner" />
                <span>Generating...</span>
              </div>
            )}

            {!generating && !result && (
              <div className="igp-placeholder">
                <ImageIcon size={48} strokeWidth={1} />
                <span>Your generated image will appear here</span>
              </div>
            )}

            {!generating && result && (
              <div className="igp-result">
                <div className="igp-img-wrap">
                  <img
                    className="igp-img"
                    src={result.url}
                    alt={result.prompt || 'Generated'}
                  />
                </div>

                <div className="igp-result-meta">
                  <GlassBadge variant="info">
                    {result.width} × {result.height}
                  </GlassBadge>
                  <GlassBadge variant="default">
                    {providerLabel(result.provider)}
                  </GlassBadge>
                  {result.noise_applied && (
                    <GlassBadge variant="info">
                      <Sparkles size={11} style={{ marginRight: 4 }} />
                      Noise filter applied
                    </GlassBadge>
                  )}
                  <a
                    className="igp-download-link"
                    href={result.url}
                    download={`generated-${Date.now()}.png`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <GlassButton variant="ghost" size="sm" icon={<Download size={14} />}>
                      Download
                    </GlassButton>
                  </a>
                </div>

                {result.revised_prompt && (
                  <div className="igp-revised-prompt">
                    Revised prompt: {result.revised_prompt}
                  </div>
                )}
              </div>
            )}
          </GlassCard>
        </div>
      </div>

      {/* Generation history */}
      <div className="igp-history-section">
        <div className="igp-history-header">
          <span className="igp-history-title">Recent Generations</span>
          <GlassButton
            variant="secondary"
            size="sm"
            icon={<RefreshCw size={13} />}
            onClick={loadData}
            disabled={historyLoading}
          >
            Refresh
          </GlassButton>
        </div>

        {historyLoading && (
          <div className="igp-loading">
            <div className="igp-spinner" />
          </div>
        )}

        {!historyLoading && history.length === 0 && (
          <div className="igp-history-empty">No generated images yet.</div>
        )}

        {!historyLoading && history.length > 0 && (
          <div className="igp-history-grid">
            {history.map(img => (
              <div key={img.id} className="igp-history-card">
                <div className="igp-history-thumb-wrap">
                  <img
                    className="igp-history-thumb"
                    src={img.url}
                    alt={img.prompt || 'Generated'}
                    onError={e => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
                <div className="igp-history-meta">
                  <span className="igp-history-prompt" title={img.prompt}>
                    {img.prompt}
                  </span>
                  <div className="igp-history-sub">
                    {img.platform && img.platform !== 'default' && (
                      <GlassBadge variant="info">
                        {img.platform.replace(/_/g, ' ')}
                      </GlassBadge>
                    )}
                    {img.created_at && (
                      <span className="igp-history-date">{formatDate(img.created_at)}</span>
                    )}
                  </div>
                </div>
                <a
                  href={img.url}
                  download={`generated-${img.id}.png`}
                  target="_blank"
                  rel="noreferrer"
                  className="igp-history-dl"
                  title="Download"
                >
                  <Download size={14} />
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageGenerationPanel;
